export type PmTaskPreview = {
  schemaVersion: 1;
  id: string;
  canonicalUrl: string;
  title: string;
  project: { id: string; name: string; iconUrl: string | null };
  createdAt: string;
  deadlineAt: string | null;
  effort: null | { unit: 'hours'; estimate: number | null; spent: number; remaining: number | null; overrun: number | null };
  assignee: null | { id: string; name: string; avatarUrl: string | null };
  status?: null | { code: string; labels: { ru: string; en: string } };
  clarifications?: null | { items: { id: number; text: string; url: string }[]; hasMore: boolean };
};

// Canonical PM TaskStatus values, including the existing upstream spelling.
const statusCodes = new Set(['wait_appraisal', 'appraisal', 'noconfirm_appraisal', 'confirm_appraisal',
  'accepted', 'pause', 'info_required', 'worked', 'wait_testing', 'test_in_developmed_enveronment',
  'testing_test', 'noconfirm_testing', 'waiting_to_live', 'testing', 'confirm_testing', 'complete']);

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
function text(value: unknown, limit: number): value is string {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= limit * 2 && Array.from(value).length <= limit && !/[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/.test(value);
}
function date(value: unknown): value is string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?\+03:00$/.test(value)
    && Number.isFinite(Date.parse(value))
    && new Date(Date.parse(value) + 3 * 60 * 60 * 1000).toISOString().slice(0, 19) === value.slice(0, 19);
}
function hours(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1_000_000_000;
}
function identifier(value: unknown): value is string {
  return typeof value === 'string' && /^[1-9]\d{0,9}$/.test(value) && Number(value) <= 2147483647;
}
/** Browser images must be served by the task-bound Chat ACL proxy, never a PM locator. */
export function pmTaskAssetPath(id: string, kind: 'project-icon' | 'assignee-avatar'): string {
  return `/v1/pm-task-previews/${id}/assets/${kind}`;
}

/** Copy only display fields; never propagate provider payloads into message state. */
export function readPmTaskPreview(value: unknown, id: string): PmTaskPreview | null {
  if (!/^[1-9]\d{0,9}$/.test(id) || Number(id) > 2147483647 || !record(value)
    || value.schemaVersion !== 1 || value.id !== id || value.canonicalUrl !== `https://pm.openstart.ru/${id}`
    || !text(value.title, 2048) || !record(value.project)
    || !date(value.createdAt) || !(value.deadlineAt === null || date(value.deadlineAt))) return null;
  const project = value.project;
  if (!identifier(project.id) || !text(project.name, 255)
    || (project.iconUrl !== null && project.iconUrl !== pmTaskAssetPath(id, 'project-icon'))) return null;
  let effort: PmTaskPreview['effort'] = null;
  if (value.effort !== null) {
    const e = value.effort;
    if (!record(e) || e.unit !== 'hours' || !hours(e.spent)) return null;
    if (e.estimate === null) {
      if (e.remaining !== null || e.overrun !== null) return null;
      effort = { unit: 'hours', estimate: null, spent: e.spent, remaining: null, overrun: null };
    } else {
      if (!hours(e.estimate) || e.estimate === 0 || !hours(e.remaining) || !hours(e.overrun)
        || Math.abs(e.remaining - Math.max(0, e.estimate - e.spent)) > 0.000001
        || Math.abs(e.overrun - Math.max(0, e.spent - e.estimate)) > 0.000001) return null;
      effort = { unit: 'hours', estimate: e.estimate, spent: e.spent, remaining: e.remaining, overrun: e.overrun };
    }
  }
  let assignee: PmTaskPreview['assignee'] = null;
  if (value.assignee !== null) {
    const a = value.assignee;
    if (!record(a) || !identifier(a.id) || !text(a.name, 255)
      || (a.avatarUrl !== null && a.avatarUrl !== pmTaskAssetPath(id, 'assignee-avatar'))) return null;
    assignee = { id: a.id, name: a.name, avatarUrl: a.avatarUrl };
  }
  const result: PmTaskPreview = {
    schemaVersion: 1, id, canonicalUrl: value.canonicalUrl, title: value.title,
    project: { id: project.id, name: project.name, iconUrl: project.iconUrl },
    createdAt: value.createdAt, deadlineAt: value.deadlineAt, effort, assignee,
  };
  // Older PM versions may omit these fields; explicitly malformed new fields fail closed.
  if (Object.hasOwn(value, 'status')) {
    if (value.status === null) result.status = null;
    else {
      const s = value.status;
      if (!record(s) || typeof s.code !== 'string' || !statusCodes.has(s.code) || !record(s.labels)
        || !text(s.labels.ru, 128) || !text(s.labels.en, 128)) return null;
      result.status = { code: s.code, labels: { ru: s.labels.ru, en: s.labels.en } };
    }
  }
  if (Object.hasOwn(value, 'clarifications')) {
    if (value.clarifications === null) result.clarifications = null;
    else {
      const c = value.clarifications;
      if (result.status?.code !== 'info_required' || !record(c) || !Array.isArray(c.items)
        || c.items.length > 3 || typeof c.hasMore !== 'boolean' || (c.hasMore && c.items.length !== 3)) return null;
      const items: NonNullable<PmTaskPreview['clarifications']>['items'] = [];
      let previous = 0;
      for (const item of c.items) {
        if (!record(item) || typeof item.id !== 'number' || !Number.isInteger(item.id)
          || item.id <= previous || item.id > 2147483647 || !text(item.text, 240)
          || item.url !== `https://pm.openstart.ru/${id}#comment-${item.id}`) return null;
        items.push({ id: item.id, text: item.text, url: item.url }); previous = item.id;
      }
      result.clarifications = { items, hasMore: c.hasMore };
    }
  }
  return result;
}
